import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import CollapseHeader from "../../../core/common/collapse-header";
import {
  fetchUserProfileById,
  updateUser,
  updateUserProfileWithPhoto,
} from "../../../api/usermanager";
import { RootState } from "../../../core/data/redux/store";
import { useSelector } from "react-redux";
import {
  validatePersonName,
  validateLocationName,
  validateAddress,
  validatePostalCode,
  validateNoHTML,
  validateNoDoubleSpacesLead,
  validateNotOnlySymbolsLead,
  validateNoRepeatedCharactersLead,
  validateNoEdgeSymbols,
} from "../../../api/leadValidation"; // Your validation file

const Profile = () => {
  const [profileData, setProfileData] = useState({
    First_name: "",
    Last_name: "",
    name: "",
    phone_no: "",
    email: "",
    Address: "",
    Country: "",
    State_Provience: "",
    City: "",
    postal_code: "",
    role: "",
    status: true,
    photo: null as File | null,
  });

  const [loading, setLoading] = useState(false);
  const [imgRefreshKey, setImgRefreshKey] = useState(Date.now());

  const token = localStorage.getItem("authToken") || "";
  const userId = useSelector((state: RootState) => state.auth.user_id);

  // Validation states
  const [firstNameError, setFirstNameError] = useState("");
  const [lastNameError, setLastNameError] = useState("");
  const [addressError, setAddressError] = useState("");
  const [countryError, setCountryError] = useState("");
  const [stateError, setStateError] = useState("");
  const [cityError, setCityError] = useState("");
  const [postalCodeError, setPostalCodeError] = useState("");

  // Track if user has interacted with each field
  const [touched, setTouched] = useState({
    First_name: false,
    Last_name: false,
    Address: false,
    Country: false,
    State_Provience: false,
    City: false,
    postal_code: false,
  });

  // Form validity
  const [isFormValid, setIsFormValid] = useState(false);

  useEffect(() => {
    const loadProfile = async () => {
      if (!userId) return;
      const data = await fetchUserProfileById(userId);
      const newProfile = {
        First_name: data?.First_name || "",
        Last_name: data?.Last_name || "",
        name: data?.name || "",
        phone_no: data?.phone_no || "",
        email: data?.email || "",
        Address: data?.Address || "",
        Country: data?.Country || "",
        State_Provience: data?.State_Provience || "",
        City: data?.City || "",
        postal_code: data?.postal_code || "",
        role: data?.role || "",
        status: data?.status ?? true,
        photo: null,
      };
      setProfileData(newProfile);

      // Set initial touched and errors for pre-filled fields
      const newTouched = {
        First_name: !!newProfile.First_name.trim(),
        Last_name: !!newProfile.Last_name.trim(),
        Address: !!newProfile.Address.trim(),
        Country: !!newProfile.Country.trim(),
        State_Provience: !!newProfile.State_Provience.trim(),
        City: !!newProfile.City.trim(),
        postal_code: !!newProfile.postal_code.trim(),
      };
      setTouched(newTouched);

      // Set initial errors and shake if pre-filled invalid
      if (newTouched.First_name) {
        const res = validatePersonName(newProfile.First_name, "First Name");
        setFirstNameError(res.message);
        if (!res.valid) {
          document
            .querySelector("#firstName")
            ?.previousElementSibling?.classList.add("pro-shake");
        }
      }
      if (newTouched.Last_name) {
        const res = validatePersonName(newProfile.Last_name, "Last Name");
        setLastNameError(res.message);
        if (!res.valid) {
          document
            .querySelector("#lastName")
            ?.previousElementSibling?.classList.add("pro-shake");
        }
      }
      if (newTouched.Address) {
        const res = validateAddress(newProfile.Address, "Address");
        setAddressError(res.message);
        if (!res.valid) {
          document
            .querySelector("#address")
            ?.previousElementSibling?.classList.add("pro-shake");
        }
      }
      if (newTouched.Country) {
        const res = validateLocationName(newProfile.Country, "Country");
        setCountryError(res.message);
        if (!res.valid) {
          document
            .querySelector("#country")
            ?.previousElementSibling?.classList.add("pro-shake");
        }
      }
      if (newTouched.State_Provience) {
        const res = validateLocationName(
          newProfile.State_Provience,
          "State / Province"
        );
        setStateError(res.message);
        if (!res.valid) {
          document
            .querySelector("#state")
            ?.previousElementSibling?.classList.add("pro-shake");
        }
      }
      if (newTouched.City) {
        const res = validateLocationName(newProfile.City, "City");
        setCityError(res.message);
        if (!res.valid) {
          document
            .querySelector("#city")
            ?.previousElementSibling?.classList.add("pro-shake");
        }
      }
      if (newTouched.postal_code) {
        const res = validatePostalCode(newProfile.postal_code, "Postal Code");
        setPostalCodeError(res.message);
        if (!res.valid) {
          document
            .querySelector("#postalCode")
            ?.previousElementSibling?.classList.add("pro-shake");
        }
      }
    };
    loadProfile();
  }, [userId]);

  // Calculate form validity
  useEffect(() => {
    const validateForm = () => {
      const first = validatePersonName(profileData.First_name, "First Name");
      const last = validatePersonName(profileData.Last_name, "Last Name");
      const addr = validateAddress(profileData.Address, "Address");
      const country = validateLocationName(profileData.Country, "Country");
      const state = validateLocationName(
        profileData.State_Provience,
        "State / Province"
      );
      const city = validateLocationName(profileData.City, "City");
      const postal = validatePostalCode(profileData.postal_code, "Postal Code");

      // Form is valid if all validations pass
      const allValid =
        first.valid &&
        last.valid &&
        addr.valid &&
        country.valid &&
        state.valid &&
        city.valid &&
        postal.valid;

      setIsFormValid(allValid);
    };

    validateForm();
  }, [profileData]);

  const handleProfileUpdate = async () => {
    if (!isFormValid) return;
    try {
      setLoading(true);
      if (!userId || !token) return;
      await updateUserProfileWithPhoto(userId, profileData);
      setImgRefreshKey(Date.now());
      setProfileData({ ...profileData, photo: null });
    } catch (error) {
      console.error("Update failed", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="row">
          <div className="col-md-12">
            <div className="page-header">
              <div className="row align-items-center">
                <div className="col-sm-4">
                  <h4 className="page-title">
                    <i className="ti ti-user-up text-primary me-2" />
                    My Profile
                  </h4>
                </div>
                <div className="col-sm-8 text-sm-end">
                  <div className="head-icons">
                    <CollapseHeader />
                  </div>
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-xl-12 col-lg-12">
                <div className="card">
                  <div className="card-body">
                    <h3 className="fw-semibold mb-3">Profile Settings</h3>
                    <form>
                      {/* Employee Information */}
                      <div className="border-bottom mb-2 pb-3">
                        <h5 className="fw-semibold mb-1">
                          Employee Information
                        </h5>
                        <p>Provide the information below</p>
                      </div>

                      <div className="row">
                        <div className="col-md-4">
                          <div className="mb-3">
                            <label
                              className={`form-label ${
                                firstNameError ? "pro-shake" : ""
                              }`}
                              htmlFor="firstName"
                            >
                              First Name <span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              id="firstName"
                              className={`form-control ${
                                firstNameError ? "is-invalid" : ""
                              }`}
                              value={profileData.First_name}
                              onChange={(e) => {
                                const value = e.target.value;
                                setProfileData({
                                  ...profileData,
                                  First_name: value,
                                });
                                setTouched({ ...touched, First_name: true });

                                const res = validatePersonName(
                                  value,
                                  "First Name"
                                );
                                setFirstNameError(
                                  value.trim() === "" ? "" : res.message
                                );

                                const label =
                                  document.querySelector(
                                    "#firstName"
                                  )?.previousElementSibling;
                                if (value.trim() === "") {
                                  label?.classList.remove("pro-shake");
                                } else {
                                  label?.classList.toggle(
                                    "pro-shake",
                                    !res.valid
                                  );
                                }
                              }}
                            />
                            {firstNameError && (
                              <div className="invalid-feedback">
                                {firstNameError}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="col-md-4">
                          <div className="mb-3">
                            <label
                              className={`form-label ${
                                lastNameError ? "pro-shake" : ""
                              }`}
                              htmlFor="lastName"
                            >
                              Last Name <span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              id="lastName"
                              className={`form-control ${
                                lastNameError ? "is-invalid" : ""
                              }`}
                              value={profileData.Last_name}
                              onChange={(e) => {
                                const value = e.target.value;
                                setProfileData({
                                  ...profileData,
                                  Last_name: value,
                                });
                                setTouched({ ...touched, Last_name: true });

                                const res = validatePersonName(
                                  value,
                                  "Last Name"
                                );
                                setLastNameError(
                                  value.trim() === "" ? "" : res.message
                                );

                                const label =
                                  document.querySelector(
                                    "#lastName"
                                  )?.previousElementSibling;
                                if (value.trim() === "") {
                                  label?.classList.remove("pro-shake");
                                } else {
                                  label?.classList.toggle(
                                    "pro-shake",
                                    !res.valid
                                  );
                                }
                              }}
                            />
                            {lastNameError && (
                              <div className="invalid-feedback">
                                {lastNameError}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Read-only fields */}
                        <div className="col-md-4">
                          <div className="mb-3">
                            <label className="form-label">
                              User Name <span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              value={profileData.name}
                              readOnly
                            />
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="mb-3">
                            <label className="form-label">
                              Phone Number{" "}
                              <span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              value={profileData.phone_no}
                              readOnly
                            />
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="mb-3">
                            <label className="form-label">
                              Email <span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              value={profileData.email}
                              readOnly
                            />
                          </div>
                        </div>
                      </div>

                      {/* Address Section */}
                      <div className="border-bottom mb-3 pb-3">
                        <h5 className="fw-semibold mb-1">Address</h5>
                        <p>Please enter the address details</p>
                      </div>

                      <div className="row">
                        <div className="col-md-12">
                          <div className="mb-3">
                            <label
                              className={`form-label ${
                                addressError ? "pro-shake" : ""
                              }`}
                              htmlFor="address"
                            >
                              Address <span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              id="address"
                              className={`form-control ${
                                addressError ? "is-invalid" : ""
                              }`}
                              value={profileData.Address}
                              onChange={(e) => {
                                const value = e.target.value;
                                setProfileData({
                                  ...profileData,
                                  Address: value,
                                });
                                setTouched({ ...touched, Address: true });

                                const res = validateAddress(value, "Address");
                                setAddressError(
                                  value.trim() === "" ? "" : res.message
                                );

                                const label =
                                  document.querySelector(
                                    "#address"
                                  )?.previousElementSibling;
                                if (value.trim() === "") {
                                  label?.classList.remove("pro-shake");
                                } else {
                                  label?.classList.toggle(
                                    "pro-shake",
                                    !res.valid
                                  );
                                }
                              }}
                            />
                            {addressError && (
                              <div className="invalid-feedback">
                                {addressError}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="col-lg-3 col-md-6">
                          <div className="mb-3">
                            <label
                              className={`form-label ${
                                countryError ? "pro-shake" : ""
                              }`}
                              htmlFor="country"
                            >
                              Country <span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              id="country"
                              className={`form-control ${
                                countryError ? "is-invalid" : ""
                              }`}
                              value={profileData.Country}
                              onChange={(e) => {
                                const value = e.target.value;
                                setProfileData({
                                  ...profileData,
                                  Country: value,
                                });
                                setTouched({ ...touched, Country: true });

                                const res = validateLocationName(
                                  value,
                                  "Country"
                                );
                                setCountryError(
                                  value.trim() === "" ? "" : res.message
                                );

                                const label =
                                  document.querySelector(
                                    "#country"
                                  )?.previousElementSibling;
                                if (value.trim() === "") {
                                  label?.classList.remove("pro-shake");
                                } else {
                                  label?.classList.toggle(
                                    "pro-shake",
                                    !res.valid
                                  );
                                }
                              }}
                            />
                            {countryError && (
                              <div className="invalid-feedback">
                                {countryError}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="col-lg-3 col-md-6">
                          <div className="mb-3">
                            <label
                              className={`form-label ${
                                stateError ? "pro-shake" : ""
                              }`}
                              htmlFor="state"
                            >
                              State / Province{" "}
                              <span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              id="state"
                              className={`form-control ${
                                stateError ? "is-invalid" : ""
                              }`}
                              value={profileData.State_Provience}
                              onChange={(e) => {
                                const value = e.target.value;
                                setProfileData({
                                  ...profileData,
                                  State_Provience: value,
                                });
                                setTouched({
                                  ...touched,
                                  State_Provience: true,
                                });

                                const res = validateLocationName(
                                  value,
                                  "State / Province"
                                );
                                setStateError(
                                  value.trim() === "" ? "" : res.message
                                );

                                const label =
                                  document.querySelector(
                                    "#state"
                                  )?.previousElementSibling;
                                if (value.trim() === "") {
                                  label?.classList.remove("pro-shake");
                                } else {
                                  label?.classList.toggle(
                                    "pro-shake",
                                    !res.valid
                                  );
                                }
                              }}
                            />
                            {stateError && (
                              <div className="invalid-feedback">
                                {stateError}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="col-lg-3 col-md-6">
                          <div className="mb-3">
                            <label
                              className={`form-label ${
                                cityError ? "pro-shake" : ""
                              }`}
                              htmlFor="city"
                            >
                              City <span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              id="city"
                              className={`form-control ${
                                cityError ? "is-invalid" : ""
                              }`}
                              value={profileData.City}
                              onChange={(e) => {
                                const value = e.target.value;
                                setProfileData({ ...profileData, City: value });
                                setTouched({ ...touched, City: true });

                                const res = validateLocationName(value, "City");
                                setCityError(
                                  value.trim() === "" ? "" : res.message
                                );

                                const label =
                                  document.querySelector(
                                    "#city"
                                  )?.previousElementSibling;
                                if (value.trim() === "") {
                                  label?.classList.remove("pro-shake");
                                } else {
                                  label?.classList.toggle(
                                    "pro-shake",
                                    !res.valid
                                  );
                                }
                              }}
                            />
                            {cityError && (
                              <div className="invalid-feedback">
                                {cityError}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="col-lg-3 col-md-6">
                          <div className="mb-3">
                            <label
                              className={`form-label ${
                                postalCodeError ? "pro-shake" : ""
                              }`}
                              htmlFor="postalCode"
                            >
                              Postal Code <span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              id="postalCode"
                              className={`form-control ${
                                postalCodeError ? "is-invalid" : ""
                              }`}
                              value={profileData.postal_code}
                              onChange={(e) => {
                                const value = e.target.value;
                                setProfileData({
                                  ...profileData,
                                  postal_code: value,
                                });
                                setTouched({ ...touched, postal_code: true });

                                const res = validatePostalCode(
                                  value,
                                  "Postal Code"
                                );
                                setPostalCodeError(
                                  value.trim() === "" ? "" : res.message
                                );

                                const label =
                                  document.querySelector(
                                    "#postalCode"
                                  )?.previousElementSibling;
                                if (value.trim() === "") {
                                  label?.classList.remove("pro-shake");
                                } else {
                                  label?.classList.toggle(
                                    "pro-shake",
                                    !res.valid
                                  );
                                }
                              }}
                            />
                            {postalCodeError && (
                              <div className="invalid-feedback">
                                {postalCodeError}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      <div>
                        <Link to="#" className="btn btn-light me-2">
                          Cancel
                        </Link>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={handleProfileUpdate}
                          disabled={loading || !isFormValid}
                        >
                          {loading ? "Saving..." : "Save Changes"}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
